//This code is heavily based on LokiJS code

// Copyright (c) 2015 TechFort <joe.minichino@gmail.com>
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import _ from "lodash";
import { DateTime, DateTimeUnit, Duration, DurationLike } from "luxon";

export type OperatorResult = [boolean, number];
export type QueryPredicate = (record:any, ...args: any[]) => OperatorResult;
export type OrderBySelector = (record:any) => any;
// export type QueryVars = Map<string, any>;
type OperatorFunction = (a: any, b: any, extra?: any) => OperatorResult;

export class QueryVars {
  public userTimeZone: string;

  // Vars:
  private user?: string;

  constructor(userTimeZone: string, user?: string) {
    this.userTimeZone = userTimeZone;
    this.user = user;
  }

  get(key: string): any {
    switch (key) {
      case "user":
        return this.user;
      default:
        throw "Unexpected variable in QueryVars";
    }
  }

  toRecord(): Record<string, any> {
    return { userTimeZone: this.userTimeZone, user: this.user };
  }

  static fromRecord(record: Record<string, any>): QueryVars {
    return new QueryVars(record.userTimeZone ?? "UTC", record.user);
  }

  getVarFingerprint(): string {
    return this.user ?? "";
  }

  getTimeZoneFingerprint(): string {
    return this.userTimeZone ?? "";
  }
}

export class NowReference {
  now: DateTime;

  constructor(userTimeZone: string) {
    this.now = DateTime.utc().setZone(userTimeZone).setZone('UTC', { keepLocalTime: true });
  }
}

export class MongoQueryParser {
  stringInsensitiveComparision: boolean;
  queryVars: QueryVars

  constructor(stringInsensitiveComparision: boolean = true, queryVars: QueryVars) {
    this.stringInsensitiveComparision = stringInsensitiveComparision;
    this.queryVars = queryVars;
  }


  find = (query: any, nowRef: NowReference): QueryPredicate => {
    let queryObject = _.cloneDeep(query) || 'getAll';
    let p: any;
    let property: string|undefined;
    let queryObjectOp;
    let obj: any;
    let operator: string = "";
    let value : any;
    let key: any;
    let filters: any[] = [];

    if (typeof queryObject === 'object') {
      for (p in queryObject) {
        obj = {};
        obj[p] = queryObject[p];
        filters.push(obj);

        if (Object.hasOwnProperty.call(queryObject, p)) {
          property = p;
          queryObjectOp = queryObject[p];
        }
      }
      // if more than one expression in single query object,
      // convert implicit $and to explicit $and
      if (filters.length > 1) {
        return this.find({ '$and': filters }, nowRef);
      }
    }

    // apply no filters if they want all
    if (!property || queryObject === 'getAll') {
      return () => [true, Infinity];
    }

    // injecting $and and $or expression tree evaluation here.
    if (property === '$and' || property === '$or' || property === '$not') {
      return this[property](queryObjectOp, nowRef);
    }

    // see if query object is in shorthand mode (assuming eq operator)
    if (queryObjectOp === null 
        || typeof queryObjectOp !== 'object' 
        || queryObjectOp instanceof Date 
        || "$var" in queryObjectOp) {
      operator = '$eq';
      value = queryObjectOp;
    } else if (queryObjectOp instanceof RegExp) {
      operator = '$regex';
      value = queryObjectOp;
    } else if (queryObjectOp != null && typeof queryObjectOp === 'object') {
      let filters = [];
      for (key in queryObjectOp) {
        if (Object.hasOwnProperty.call(queryObjectOp, key)) {
          operator = key;
          value = queryObjectOp[key];
          filters.push({ [property]: { [operator]: value } });
        }
      }
      if (filters.length > 1) {
        return this.find({ '$and': filters }, nowRef);
      }
    } else {
      throw new Error('Do not know what you want to do.');
    }

    if (operator === '$regex' || (value != null && typeof value === 'object')) {
      [operator, value] = this.precompileQuery(operator, value, nowRef);
    }

    // if an index exists for the property being queried against, use it
    // for now only enabling where it is the first filter applied and prop is indexed


    // opportunistically speed up $in searches from O(n*m) to O(n*log m)
    if (operator === '$in' && Array.isArray(value) && typeof Set !== 'undefined') {
      if (this.stringInsensitiveComparision)
        value = value.map(x => {
          if (typeof x === 'string')
            return lowerString(x);
          else
            return x;
        });
      value = new Set(value);
      operator = '$inSet';
    } else {
      if (this.stringInsensitiveComparision && typeof value === 'string')
        value = lowerString(value);
    }

    if (operator === undefined) {
      throw new Error(`invalid mongo query: there is no query operator for property ${property}`);
    }

    return this.getPredicate(property, operator, value);
  }

  getPredicate = (property: string, operator: string, value: any) => {
    // the comparison function
    let fun: OperatorFunction;

    var lokiOp = (LokiOps as any)[operator];
    if (lokiOp === undefined) {
      throw new Error(`unknown mongo query operator ${operator}`);
    }

    if (this.stringInsensitiveComparision) {
      fun = (recordValue, value, record) => {
        if (typeof recordValue === 'string')
          recordValue = lowerString(recordValue);

        return lokiOp(recordValue, value, record);
      };
    } else {
      fun = lokiOp;
    }

    var usingDotNotation = (property.indexOf('.') !== -1);
    if (usingDotNotation) {
      let path = property.split('.');
      return (record: any) => dotSubScan(record, path, fun, value, record, 0);
    } else {
      return (record: any) => fun(record[property!], value, record);
    }
  }

  /**
   * findOr() - oversee the operation of OR'ed query expressions.
   * @param {array} expressionArray - array of expressions
   * @returns {predicate} predicate function for this operation
   */
  findOr = (expressionArray: any[], nowRef: NowReference): QueryPredicate => {
    let predicates = expressionArray.map(x => this.find(x, nowRef));

    const orPredicate = (predicate: QueryPredicate, record: any) => predicate(record);

    return (record: any) => temporal_some(predicates, orPredicate, record);
  }

  $or = this.findOr;

  /**
   * findAnd() - oversee the operation of AND'ed query expressions.
   * @param {array} expressionArray - array of expressions
   * @returns {predicate} predicate function for this operation 
   */
  findAnd = (expressionArray: any[], nowRef: NowReference): QueryPredicate => {
    let predicates = expressionArray.map(x => this.find(x, nowRef));

    const andPredicate = (predicate: QueryPredicate, record: any) => predicate(record);

    return (record: any) => temporal_every(predicates, andPredicate, record);
  };

  $and = this.findAnd;

  findNot = (expression: any, nowRef: NowReference): QueryPredicate => {
    let predicate = this.find(expression, nowRef);
    return (record: any) => temporal_not(record, predicate);
  }

  $not = this.findNot;

  // precompile recursively
  precompileQuery = (operator: string, value: any, nowRef: NowReference): any => {
    // for regex ops, precompile
    if (operator === '$regex') {
      if (Array.isArray(value)) {
        value = new RegExp(value[0], value[1]);
      } else if (!(value instanceof RegExp)) {
        value = new RegExp(value);
      }
    } 
    else if (operator === "$elemMatch") {
        let predicate = this.find(value, nowRef);
        return ["$elemMatch_predicate",  predicate];
    } 
    else if (value != null && typeof value === 'object') {
      if ("$time" in value) {
        [operator, value] = this.compileTimePredicate(operator, value["$time"], nowRef);
      } else if ("$var" in value) {
        value = this.queryVars.get(value["$var"]);
        return [operator, value];
      } else if (!Array.isArray(value)) {
        for (var key in value) {
          if (Object.hasOwnProperty.call(value, key) && (key === '$regex' || typeof value[key] === 'object')) {
            let [newOperator, newValue] = this.precompileQuery(key, value[key], nowRef);
            //replace key with new operator
            value = {...value};
            delete value[key];
            value[newOperator] = newValue;
          }
        }
      }
    }

    return [operator, value];
  }

  compileTimePredicate = (operator: string, expression: string, nowRef: NowReference): [string, QueryPredicate] => {
    let time_predicate: QueryPredicate = () => [false, Infinity];

    // Regex patterns
    const baseTimePattern = /(\$now|\$utc_now)|(\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}Z?)?)/;
    const modifierPattern = /(\.startOfDay|\.endOfDay|\.startOfMonth|\.endOfMonth|\.startOfYear|\.endOfYear)/;
    const operationPattern = /([+-])?\s*(\d+)(sec|min|h|d|m|y)/g;

    let baseTimeMatch = expression.match(baseTimePattern);
    if (baseTimeMatch == null)
      throw new Error(`$time operator require to specify expression with exactly one base time: one of forms of $now of ISO time string`);

    let baseTime = (baseTimeMatch ?? [])[0];
    let modifier = (expression.match(modifierPattern) ?? [])[0];
    let operations = Array.from(expression.matchAll(operationPattern));

    // Convert baseTime to Unix timestamp
    //let timestamp = convertBaseTimeToTimestamp(baseTime, modifier);

    if (baseTime === '$now' || baseTime === '$utc_now') {
      // Apply operations
      let delta = Duration.fromMillis(0);

      operations.forEach(operation => {
        let sign = operation[1] ?? '+';
        let value = parseInt(operation[2]);
        let unit = operation[3];

        if (sign === '-')
          value = -value;

        switch (unit) {
          case 'y':
            delta = delta.plus({ year: value });
            break;
          case 'm':
            delta = delta.plus({ month: value });
            break;
          case 'd':
            delta = delta.plus({ day: value });
            break;
          case 'h':
            delta = delta.plus({ hour: value });
            break;
          case 'min':
            delta = delta.plus({ minute: value });
            break;
          case 'sec':
            delta = delta.plus({ second: value });
            break;
        }
      });

      const applyStartGranularity = (changeTime: DateTime, base: DateTime, granularity: DateTimeUnit) => {
        let granularChangeTime = changeTime.startOf(granularity);
        if (+changeTime !== +granularChangeTime) {
          changeTime = granularChangeTime.plus({[granularity]:1});
        }
        base = base.startOf(granularity);
        return [changeTime, base];
      }

      const applyEndGranularity = (changeTime: DateTime, base: DateTime, granularity: DateTimeUnit) => {
        let granularChangeTime = changeTime.endOf(granularity);
        if (+changeTime !== +granularChangeTime) {
          changeTime = granularChangeTime.startOf(granularity);
        }
        base = base.endOf(granularity);
        return [changeTime, base];
      }

      let applyModifier = (changeTime: DateTime, base: DateTime) => [changeTime, base];
      if (modifier) {
        switch (modifier) {
          case '.startOfDay':
            applyModifier = (changeTime, base) => applyStartGranularity(changeTime, base, 'day');
            break;
          case '.endOfDay':
            applyModifier = (changeTime, base) => applyEndGranularity(changeTime, base, 'day');
            break;
          case '.startOfMonth':
            applyModifier = (changeTime, base) => applyStartGranularity(changeTime, base, 'month');
            break;
          case '.endOfMonth':
            applyModifier = (changeTime, base) => applyEndGranularity(changeTime, base, 'month');
            break;
          case '.startOfYear':
            applyModifier = (changeTime, base) => applyStartGranularity(changeTime, base, 'year');
            break;
          case '.endOfYear':
            applyModifier = (changeTime, base) => applyEndGranularity(changeTime, base, 'year');
            break;

          default:
            throw new Error(`unsupported $now modifier: ${modifier}`);
            break;
        }
      }

      const lt_predicate = (value: DateTime): OperatorResult => {
        let changeTime = solveInterval(delta, value).plus({seconds:1});

        let base = nowRef.now;
        if (baseTime === '$utc_now')
          base = base.toUTC();

        [changeTime, base] = applyModifier(changeTime, base);

        let result = value < base.plus(delta);

        if (changeTime <= nowRef.now)
          return [result, Infinity];
        else
          return [result, changeTime.toSeconds()];
      }

      const gt_predicate = (value: DateTime): OperatorResult => {
        let changeTime = solveInterval(delta, value);
        let base = nowRef.now;
        if (baseTime === '$utc_now')
          base = base.toUTC();

        [changeTime, base] = applyModifier(changeTime, base);

        let result = value > base.plus(delta);

        if (changeTime <= nowRef.now)
          return [result, Infinity];
        else
          return [result, changeTime.toSeconds()];
      }

      switch (operator) {
        case '$lt':
          time_predicate = lt_predicate
          break;
        case '$gt':
          time_predicate = gt_predicate
          break;
        case '$lte':
          time_predicate = (value) => {
            const [otherValue, validDue] = gt_predicate(value);
            return [!otherValue, validDue];
          }
          break;
        case '$gte':
          time_predicate = (value) => {
            const [otherValue, validDue] = lt_predicate(value);
            return [!otherValue, validDue];
          }
          break;
        default:
          throw new Error(`unsupported operator for relative time: ${operator}`);
      }
    }

    return ["$time_predicate", time_predicate];
  }
}

/**
* solve min{x s.t. x.plus(delta) >= b}
*
* @param {Duration} delta - delta constant
* @param {DateTime} b - b constant
*/
function solveInterval(delta: DurationLike, b: DateTime) {
  let dayDelta = Duration.fromDurationLike({ days: 1 });

  let x = b.minus(delta);

  if (x.plus(delta) < b) {
    do {
      x = x.plus(dayDelta);
    } while (x.plus(delta) < b)
  } else {
    let r = x;
    do {
      x = r;
      r = r.minus(dayDelta);
    } while (r.plus(delta) >= b);
  }

  return x;
}

/**
* dotSubScan - helper function used for dot notation queries.
*
* @param {object} root - object to traverse
* @param {array} paths - array of properties to drill into
* @param {function} fun - evaluation function to test with
* @param {any} value - comparative value to also pass to (compare) fun
* @param {any} extra - extra arg to also pass to compare fun
* @param {number} poffset - index of the item in 'paths' to start the sub-scan from
*/
function dotSubScan(root: any, paths: string[], fun: OperatorFunction, value: any, extra:any, poffset?:number): OperatorResult {
  let pathOffset = poffset ?? 0;

  while (true) {

    let path = paths[pathOffset];
    let element;
    if (root !== null && typeof root === 'object' && path in root) {
      element = root[path];
    }

    if (pathOffset + 1 >= paths.length) {
      // if we have already expanded out the dot notation,
      // then just evaluate the test function and value on the element
      return fun(element, value, extra);
    }

    if (Array.isArray(element)) {
      let min_time = Infinity;

      for (var index = 0, len = element.length; index < len; index += 1) {
        let [valueFound, validDue] = dotSubScan(element[index], paths, fun, value, extra, pathOffset + 1);
        min_time = Math.min(min_time, validDue ?? Infinity);
        if (valueFound === true) {
          return [true, validDue];
        }
      }
      return [false, min_time];
    } else {
      pathOffset++;
      root = element;
    }
  }
}

function containsCheckFn(a: any[]) {
  if (typeof a === 'string' || Array.isArray(a)) {
    return function (b:any) {
      return a.indexOf(b) !== -1;
    };
  } else if (typeof a === 'object' && a !== null) {
    return function (b:any) {
      return Object.hasOwnProperty.call(a, b);
    };
  }
  return null;
}

export function doQueryOp(val: any, op: any, record: any): OperatorResult {
  return temporal_every(Object.keys(op).filter(p => Object.hasOwnProperty.call(op, p)), (p: string) => (LokiOps as any)[p](val, op[p], record));
}

function temporal_some(array: any[], time_predicate: QueryPredicate, ...args: any[]): OperatorResult {
  let result = false;
  let min_time = Infinity;
  let max_time = -Infinity;

  for (let e of array) {
    let [value, validDue] = time_predicate(e, ...args);
    if (value)
      max_time = Math.max(max_time, validDue ?? -Infinity);
    min_time = Math.min(min_time, validDue ?? Infinity);
    result ||= value;
  }
  const time = result ? max_time : min_time;
  return [result, time];
}

function temporal_every(array: any[], time_predicate: QueryPredicate, ...args: any[]): OperatorResult {
  let result = true;
  let min_time = Infinity;
  let max_time = -Infinity;

  for (let e of array) {
    let [value, validDue] = time_predicate(e, ...args);
    if (!value)
      max_time = Math.max(max_time, validDue ?? -Infinity);
    min_time = Math.min(min_time, validDue ?? Infinity);
    result &&= value;
  }
  const time = result ? min_time : max_time;
  return [result, time];
};

function temporal_not(value: any, predicate: QueryPredicate, ...args: any[]): OperatorResult {
  let [result, validDue] = predicate(value, ...args);
  return [!result, validDue];
};

export var LokiOps = {
  $time_predicate: function (value: any, predicate: QueryPredicate): OperatorResult {
    if (value == null || Number.isNaN(value))
      return [false, Infinity];
    else if (typeof value === 'string')
      value = DateTime.fromISO(value, { zone: 'utc' });
    else if (typeof value === 'number')
      value = DateTime.fromSeconds(value, { zone: 'utc' });
    else if (value != null && typeof value === 'object')
      value = DateTime.fromJSDate(value, { zone: 'utc' });
    else
      return [false, Infinity];

    return predicate(value);
  },

  // comparison operators
  // a is the value in the collection
  // b is the query value
  $eq: function (a: any, b: any): OperatorResult {
    if (b == null)
      return [a == null, Infinity];

    if (b === false || b === true)
      return [!!a === b, Infinity];

    return [a === b, Infinity];
  },

  // abstract/loose equality
  $aeq: function (a: any, b: any): OperatorResult {
    return [a == b, Infinity];
  },

  $ne: function (a: any, b: any): OperatorResult {
    // ecma 5 safe test for NaN
    if (b !== b) {
      // ecma 5 test value is not NaN
      return [(a === a), Infinity];
    }

    if (b == null)
      return [a != null, Infinity];

    return [a !== b, Infinity];
  },

  // abstract/loose non equality
  $ane: function (a: any, b: any): OperatorResult {
    // ecma 5 safe test for NaN
    if (b !== b) {
      // ecma 5 test value is not NaN
      return [(a === a), Infinity];
    }

    return [a != b, Infinity];
  },

  // date equality / loki abstract equality test
  $dteq: function (a: any, b: any): OperatorResult {
    return [Comparators.aeq(a, b), Infinity];
  },

  // loki comparisons: return identical unindexed results as indexed comparisons
  $gt: function (a: any, b: any): OperatorResult {
    return [Comparators.gt(a, b, false), Infinity];
  },

  $gte: function (a: any, b: any): OperatorResult {
    return [Comparators.gt(a, b, true), Infinity];
  },

  $lt: function (a: any, b: any): OperatorResult {
    return [Comparators.lt(a, b, false), Infinity];
  },

  $lte: function (a: any, b: any): OperatorResult {
    return [Comparators.lt(a, b, true), Infinity];
  },

  // lightweight javascript comparisons
  $jgt: function (a: any, b: any): OperatorResult {
    return [a > b, Infinity];
  },

  $jgte: function (a: any, b: any): OperatorResult {
    return [a >= b, Infinity];
  },

  $jlt: function (a: any, b: any): OperatorResult {
    return [a < b, Infinity];
  },

  $jlte: function (a: any, b: any): OperatorResult {
    return [a <= b, Infinity];
  },

  // ex : coll.find({'orderCount': {$between: [10, 50]}});
  $between: function (a: any, vals: any[]): OperatorResult {
    if (a === undefined || a === null) return [false, Infinity];
    return [(Comparators.gt(a, vals[0], true) && Comparators.lt(a, vals[1], true)), Infinity];
  },

  $jbetween: function (a: any, vals: any[]): OperatorResult {
    if (a === undefined || a === null) return [false, Infinity];
    return [(a >= vals[0] && a <= vals[1]), Infinity];
  },

  $in: function (a: any, b: any): OperatorResult {
    return [b.indexOf(a) !== -1, Infinity];
  },

  $inSet: function (a: any, b: any): OperatorResult {
    return [b.has(a), Infinity];
  },

  $nin: function (a: any, b: any): OperatorResult {
    return [b.indexOf(a) === -1, Infinity];
  },

  $keyin: function (a: any, b: any): OperatorResult {
    return [a in b, Infinity];
  },

  $nkeyin: function (a: any, b: any): OperatorResult {
    return [!(a in b), Infinity];
  },

  $definedin: function (a: any, b: any): OperatorResult {
    return [b[a] !== undefined, Infinity];
  },

  $undefinedin: function (a: any, b: any): OperatorResult {
    return [b[a] === undefined, Infinity];
  },

  $regex: function (a: any, b: any): OperatorResult {
    return [b.test(a), Infinity];
  },

  $containsString: function (a: any, b: any): OperatorResult {
    return [(typeof a === 'string') && (a.indexOf(b) !== -1), Infinity];
  },

  $containsNone: function (a: any, b: any): OperatorResult {
    let [result, validDue] = LokiOps.$containsAny(a, b);
    return [!result, validDue];
  },

  $containsAny: function (a: any, b: any): OperatorResult {
    var checkFn = containsCheckFn(a);
    if (checkFn !== null) {
      return [(Array.isArray(b)) ? (b.some(checkFn)) : (checkFn(b)), Infinity];
    }
    return [false, Infinity];
  },

  $contains: function (a: any, b: any): OperatorResult {
    var checkFn = containsCheckFn(a);
    if (checkFn !== null) {
      return [(Array.isArray(b)) ? (b.every(checkFn)) : (checkFn(b)), Infinity];
    }
    return [false, Infinity];
  },

  $elemMatch_predicate: (value: any, predicate: QueryPredicate) => {
    if (Array.isArray(value)) {
        return temporal_some(value, predicate);
    }
    return [false, Infinity];
  },

  $elemMatch: function (a: any, b: any): OperatorResult {
    throw Error("$elemMatch must be precompiled");
  },

  $type: function (a: any, b: any, record: any): OperatorResult {
    let type: string = typeof a;
    if (type === 'object') {
      if (Array.isArray(a)) {
        type = 'array';
      } else if (a instanceof Date) {
        type = 'date';
      }
    }
    return (typeof b !== 'object') ? [(type === b), Infinity] : doQueryOp(type, b, record);
  },

  $finite: function (a: any, b: any): OperatorResult {
    return [(b === isFinite(a)), Infinity];
  },

  $size: function (a: any, b: any, record: any): OperatorResult {
    if (Array.isArray(a) || a == null) {
      const size = a?.length ?? 0;
      return (typeof b !== 'object') ? [(size === b), Infinity] : doQueryOp(size, b, record);
    }
    return [false, Infinity];
  },

  $len: function (a: any, b: any, record: any): OperatorResult {
    if (typeof a === 'string' || a == null) {
      const size = a?.length ?? 0;
      return (typeof b !== 'object') ? [(size === b), Infinity] : doQueryOp(size, b, record);
    }
    return [false, Infinity];
  },

  $where: function (a: any, b: any): OperatorResult {
    return [b(a) === true, Infinity];
  },

  $time_where: function (a: any, b: QueryPredicate): OperatorResult {
    return b(a);
  },

  // field-level logical operators
  // a is the value in the collection
  // b is the nested query operation (for '$not')
  //   or an array of nested query operations (for '$and' and '$or')
  $not: function (a: any, b: any, record: any): OperatorResult {
    // throw new Error("The $not operator cannot be used exactly like that. Most likely the request is incorrect.");

    let [value, validDue] = doQueryOp(a, b, record);
    return [!value, validDue];
  },

  $and: function (a: any, b: any, record: any): OperatorResult {
    throw new Error("The $and operator cannot be used exactly like that. Most likely the request is incorrect.");

    return temporal_every(b, (e) => doQueryOp(a, e, record));
    // for (var idx = 0, len = b.length; idx < len; idx += 1) {
    //   if (!doQueryOp(a, b[idx], record)) {
    //     return false;
    //   }
    // }
    // return true;
  },

  $or: function (a: any, b: any, record: any): OperatorResult {
    throw new Error("The $or operator cannot be used exactly like that. Most likely the request is incorrect.");

    return temporal_some(b, (e) => doQueryOp(a, e, record));
    // for (var idx = 0, len = b.length; idx < len; idx += 1) {
    //   if (doQueryOp(a, b[idx], record)) {
    //     return true;
    //   }
    // }
    // return false;
  },

  $exists: function (a: any, b: any): OperatorResult {
    if (b) {
      return [a !== undefined, Infinity];
    } else {
      return [a === undefined, Infinity];
    }
  }
};

export function lowerString(str: string): string {
  return str.trim().toLocaleLowerCase();
}


/** Helper function for determining 'loki' abstract equality which is a little more abstract than ==
 *     aeqHelper(5, '5') === true
 *     aeqHelper(5.0, '5') === true
 *     aeqHelper(new Date("1/1/2011"), new Date("1/1/2011")) === true
 *     aeqHelper({a:1}, {z:4}) === true (all objects sorted equally)
 *     aeqHelper([1, 2, 3], [1, 3]) === false
 *     aeqHelper([1, 2, 3], [1, 2, 3]) === true
 *     aeqHelper(undefined, null) === true
 */
function aeqHelper(prop1: any, prop2: any): boolean {
  var cv1, cv2, t1, t2;

  if (prop1 === prop2) return true;

  // 'falsy' and Boolean handling
  if (!prop1 || !prop2 || prop1 === true || prop2 === true || prop1 !== prop1 || prop2 !== prop2) {
    // dates and NaN conditions (typed dates before serialization)
    switch (prop1) {
      case undefined: t1 = 1; break;
      case null: t1 = 1; break;
      case false: t1 = 3; break;
      case true: t1 = 4; break;
      case "": t1 = 5; break;
      default: t1 = (prop1 === prop1) ? 9 : 0; break;
    }

    switch (prop2) {
      case undefined: t2 = 1; break;
      case null: t2 = 1; break;
      case false: t2 = 3; break;
      case true: t2 = 4; break;
      case "": t2 = 5; break;
      default: t2 = (prop2 === prop2) ? 9 : 0; break;
    }

    // one or both is edge case
    if (t1 !== 9 || t2 !== 9) {
      return (t1 === t2);
    }
  }

  // Handle 'Number-like' comparisons
  cv1 = Number(prop1);
  cv2 = Number(prop2);

  // if one or both are 'number-like'...
  if (cv1 === cv1 || cv2 === cv2) {
    return (cv1 === cv2);
  }

  // not strict equal nor less than nor gt so must be mixed types, convert to string and use that to compare
  cv1 = prop1.toString();
  cv2 = prop2.toString();

  return (cv1 == cv2);
}

/** Helper function for determining 'less-than' conditions for ops, sorting, and binary indices.
 *     In the future we might want $lt and $gt ops to use their own functionality/helper.
 *     Since binary indices on a property might need to index [12, NaN, new Date(), Infinity], we
 *     need this function (as well as gtHelper) to always ensure one value is LT, GT, or EQ to another.
 */
function ltHelper(prop1: any, prop2: any, equal: boolean): boolean {
    if (prop1 == null || prop2 == null)
        return false;

    if (equal)
        return prop1 <= prop2;
    else
        return prop1 < prop2;
}

function gtHelper(prop1: any, prop2: any, equal:boolean): boolean {
    if (prop1 == null || prop2 == null)
        return false;

    if (equal)
        return prop1 >= prop2;
    else
        return prop1 > prop2;
}

var Comparators = {
  aeq: aeqHelper,
  lt: ltHelper,
  gt: gtHelper
};
